import type { ReactNode } from 'react';

import { formatDateTime, formatMoney } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';

import type {
  BrokerDefaultLimit,
  ConsultationLimit,
  ConsultationLimitsCost,
} from './api';
import { rotuloDoEscopo, textoDoLimite } from './labels';

/**
 * Pecas de leitura dos limites de consulta, usadas pela administracao da plataforma,
 * que edita, e pela empresa, que so le (ADR 0098).
 */

/** Situacao do limite no periodo. */
export function SituacaoDoLimite({ limite }: { limite: ConsultationLimit }) {
  if (limite.unlimited) return <Badge tone="warning">Sem limite</Badge>;
  if (!limite.enforced) {
    return (
      <Badge tone="neutral">
        {limite.unblocked_in_period ? 'Desbloqueado no periodo' : 'So informa'}
      </Badge>
    );
  }
  if (limite.reached) return <Badge tone="danger">Atingido</Badge>;
  return <Badge tone="success">Dentro do limite</Badge>;
}

/** De onde vem o limite da empresa, com quem definiu e a justificativa. */
function origemDoLimite(limite: ConsultationLimit): string {
  if (limite.source !== 'plataforma') {
    return 'Padrao dos parametros do motor de credito: nenhum limite foi definido para a empresa.';
  }
  const quando = limite.updated_at ? ` em ${formatDateTime(limite.updated_at)}` : '';
  const quem = limite.updated_label ? ` por ${limite.updated_label}` : '';
  const justificativa = limite.reason ? ` Justificativa: ${limite.reason}` : '';
  return `Definido pela administracao da plataforma${quem}${quando}.${justificativa}`;
}

/** Limite, uso e saldo da empresa. */
export function ResumoDaEmpresa({
  limite,
  acoes,
}: {
  limite: ConsultationLimit;
  acoes?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <dl className="grid flex-1 gap-3 text-sm sm:grid-cols-4">
          <div>
            <dt className="text-xs text-ink-500">Limite da empresa</dt>
            <dd className="mt-0.5 font-medium text-ink-900">{textoDoLimite(limite)}</dd>
          </div>
          <div>
            <dt className="text-xs text-ink-500">Usadas no mes</dt>
            <dd className="mt-0.5 font-medium tabular-nums text-ink-900">
              {limite.used}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-ink-500">Restam</dt>
            <dd className="mt-0.5 font-medium tabular-nums text-ink-900">
              {limite.remaining ?? 'Sem limite'}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-ink-500">Situacao</dt>
            <dd className="mt-0.5">
              <SituacaoDoLimite limite={limite} />
            </dd>
          </div>
        </dl>
        {acoes ? <div className="flex flex-wrap gap-2">{acoes}</div> : null}
      </div>
      <p className="text-2xs text-ink-500">{origemDoLimite(limite)}</p>
      {limite.unlimited ? (
        <Alert tone="warning" title="Empresa sem limite de quantidade">
          A consulta real pode ser cobrada, e nenhuma quantidade barra as consultas
          desta empresa. Os limites de corretor e de tomador e o teto financeiro dos
          parametros continuam valendo.
        </Alert>
      ) : null}
    </div>
  );
}

/** O limite que vale para a corretora sem limite proprio (ADR 0102). */
export function PadraoPorCorretor({
  padrao,
  acoes,
}: {
  padrao: BrokerDefaultLimit;
  acoes?: ReactNode;
}) {
  const quando = padrao.updated_at ? ` em ${formatDateTime(padrao.updated_at)}` : '';
  const quem = padrao.updated_label ? ` por ${padrao.updated_label}` : '';
  const justificativa = padrao.reason ? ` Justificativa: ${padrao.reason}` : '';
  return (
    <div className="flex flex-col gap-1 rounded-md border border-line p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <dl className="text-sm">
          <dt className="text-xs text-ink-500">Padrao por corretor</dt>
          <dd className="mt-0.5 font-medium text-ink-900">
            {padrao.limit} consultas por mes
          </dd>
        </dl>
        {acoes ? <div className="flex flex-wrap gap-2">{acoes}</div> : null}
      </div>
      <p className="text-2xs text-ink-500">
        Vale para a corretora sem limite proprio, nas consultas pedidas por ela.{' '}
        {padrao.source === 'plataforma'
          ? `Definido pela administracao da plataforma${quem}${quando}.${justificativa}`
          : 'Nenhum valor foi definido para a empresa: vale o padrao.'}
      </p>
    </div>
  );
}

/** Custo estimado do mes. Sem preco, diz que o custo e desconhecido. */
export function CustoDoMes({ custo }: { custo: ConsultationLimitsCost }) {
  return (
    <dl className="grid gap-3 rounded-md border border-line p-3 text-sm sm:grid-cols-3">
      <div>
        <dt className="text-xs text-ink-500">Custo do mes ate agora</dt>
        <dd className="mt-0.5 font-medium tabular-nums text-ink-900">
          {formatMoney(custo.month_spent)}
        </dd>
      </div>
      <div>
        <dt className="text-xs text-ink-500">Maior preco vigente</dt>
        <dd className="mt-0.5 font-medium tabular-nums text-ink-900">
          {custo.unit_price ? formatMoney(custo.unit_price) : 'Nenhum preco cadastrado'}
        </dd>
      </div>
      <div>
        <dt className="text-xs text-ink-500">Custo estimado do mes</dt>
        <dd className="mt-0.5 font-medium tabular-nums text-ink-900">
          {custo.estimated_max
            ? `Ate ${formatMoney(custo.estimated_max)}`
            : custo.unit_price
              ? 'Sem teto: a empresa esta sem limite'
              : 'Desconhecido: sem preco'}
        </dd>
      </div>
    </dl>
  );
}

/** Colunas comuns da lista de limites de corretor e de tomador. */
function colunasDosLimites(): Column<ConsultationLimit>[] {
  return [
    {
      key: 'escopo',
      header: 'Tipo',
      render: (limite) => rotuloDoEscopo[limite.scope],
    },
    {
      key: 'quem',
      header: 'Quem',
      render: (limite) => (
        <div className="min-w-0">
          <p className="truncate text-ink-900">{limite.subject_label}</p>
          <p className="truncate text-2xs text-ink-500">{limite.subject_document}</p>
        </div>
      ),
    },
    {
      key: 'limite',
      header: 'Limite',
      render: (limite) => <span className="tabular-nums">{textoDoLimite(limite)}</span>,
    },
    {
      key: 'uso',
      header: 'Uso',
      align: 'right',
      render: (limite) => <span className="tabular-nums">{limite.used}</span>,
    },
    {
      key: 'restam',
      header: 'Restam',
      align: 'right',
      render: (limite) => (
        <span className="tabular-nums">{limite.remaining ?? '-'}</span>
      ),
    },
    {
      key: 'situacao',
      header: 'Situacao',
      render: (limite) => <SituacaoDoLimite limite={limite} />,
    },
    {
      key: 'justificativa',
      header: 'Justificativa',
      hideBelow: 'md',
      render: (limite) => <span className="text-ink-700">{limite.reason || '-'}</span>,
    },
  ];
}

/** Lista dos limites em vigor de corretor e de tomador. */
export function ListaDeLimites({
  itens,
  carregando,
  colunasExtras = [],
}: {
  itens: ConsultationLimit[];
  carregando: boolean;
  colunasExtras?: Column<ConsultationLimit>[];
}) {
  return (
    <DataTable
      columns={[...colunasDosLimites(), ...colunasExtras]}
      items={itens}
      keyOf={(limite) => `${limite.scope}:${limite.subject_id}`}
      loading={carregando}
      empty={
        <EmptyState
          icon="coins"
          title="Nenhum limite de corretor ou de tomador"
          description="Enquanto nao houver, so o limite da empresa pesa sobre as consultas."
        />
      }
    />
  );
}
